import { createTheme, ThemeOptions } from '@material-ui/core/styles'
import { TaskPriority, TaskState } from 'schema'
declare module '@material-ui/core/styles/createPalette' {
  interface TypeBackground {
    dark: string
    light: string
    backdrop: string
  }

  interface PriorityPalette {
    main: string
    light: string
  }

  interface StatePalette {
    main: string
    light: string
  }
  interface Palette {
    priorities: { [x in TaskPriority]: PriorityPalette }
    states: { [x in TaskState]: StatePalette }

    inputs: {
      border: {
        primary: string
      }
      background: {
        primary: string
      }
      hover: {
        [x: number]: string
      }
      label: {
        bold: number
        semibold: number
        regular: number
        light: number
        color: {
          light: string
          regular: string
          dark: string
          link: string
        }
      }
    }
    ternary: {
      main: string
    }
  }

  interface PaletteOptions {
    priorities: { [x in TaskPriority]: PriorityPalette }
    states: { [x in TaskState]: StatePalette }

    inputs: {
      border: {
        primary: string
      }
      background: {
        primary: string
      }
      hover: {
        [x: number]: string
      }
      label: {
        bold: number
        semibold: number
        regular: number
        light: number
        color: {
          light: string
          regular: string
          dark: string
          link: string
        }
      }
    }
    ternary: {
      main: string
    }
  }
}

const theme = createTheme({
  props: {
    MuiTooltip: {
      enterTouchDelay: 0
    }
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        html: {
          fontSize: 14
        },
        body: {
          overflowX: 'hidden',
          overflowY: 'auto',
          minHeight: '100vh'
        },
        a: {
          color: '#588FFB',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline'
          }
        }
      }
    },
    MuiBackdrop: {
      root: {
        backgroundColor: '#B1BACA'
      }
    }
  },
  typography: {
    htmlFontSize: 14,
    fontSize: 14,
    fontFamily: "'Open Sans', sans-serif",
    h1: {
      fontWeight: 700,
      fontSize: '2.143rem',
      lineHeight: 1.366
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.71rem',
      lineHeight: 1.125
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.43rem',
      lineHeight: 1.35
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.286rem',
      lineHeight: 1.33
    },
    h5: {
      fontWeight: 700,
      fontSize: '1.07rem',
      lineHeight: 1.33
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: 1.125
    },
    subtitle1: {
      fontSize: '0.929rem',
      lineHeight: 1.54
    },
    subtitle2: {
      fontSize: '0.929rem',
      lineHeight: 1.54,
      fontWeight: 600
    },
    caption: {
      fontSize: '0.857rem',
      fontWeight: 400,
      lineHeight: 1.33
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.33
    },
    body2: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: 1.33
    }
  },
  palette: {
    background: {
      default: '#FFFFFF',
      dark: '#391E49',
      light: '#F3F5FA',
      backdrop: '#B1BACA'
    },
    primary: {
      main: '#588FFB',
      light: '#5e6ed4',
      dark: '#1E367C'
    },
    secondary: {
      main: '#555671',
      dark: '#222D39',
      light: '#9C9EC0'
    },
    ternary: {
      main: '#4E2F60'
    },
    grey: {
      50: '#f0f2f9',
      100: '#F3F5FA',
      200: '#DBDCE0',
      300: '#96969E',
      400: '#6E6D73',
      500: '#4B536A',
      600: '#404962',
      700: '#3f5169',
      800: '#303f53',
      900: '#070819'
    },
    success: {
      main: '#29B66C',
      light: '#D6F2E8'
    },
    error: {
      main: '#D62965',
      light: '#DF7E9F',
      dark: '#CF5371'
    },
    warning: {
      main: '#FEEFD0', // TODO: Change to real value if found in design
      light: '#FEEFD0',
      dark: '#F2AB6D'
    },
    inputs: {
      border: {
        primary: '#EBEBEB'
      },
      background: {
        primary: '#FFFFFF'
      },
      hover: {
        100: '#F3F5FA'
      },
      label: {
        bold: 700,
        semibold: 600,
        regular: 500,
        light: 400,
        color: {
          regular: '#7D7C83',
          light: '#96969E',
          dark: '#222D39',
          link: '#588FFB'
        }
      }
    },
    priorities: {
      low: {
        main: '#679b48',
        light: '#c2d7b6'
      },
      medium: {
        main: '#D99E2E',
        light: '#FEEFD0'
      },
      high: {
        main: '#d9572e',
        light: '#FED6D0'
      }
    },
    states: {
      todo: {
        main: '#5996d3',
        light: '#cde0f2'
      },
      in_progress: {
        main: '#D99E2E',
        light: '#FEEFD0'
      },
      in_review: {
        main: '#7d59d3',
        light: '#d8cdf2'
      },
      done: {
        main: '#679b48',
        light: '#c2d7b6'
      },
      canceled: {
        main: '#D62965',
        light: '#DF7E9F'
      }
    }
  },
  shadows: [
    'none',
    ...Array(25)
      .fill(null)
      .map((_, i) => `0px 2px ${10 + Math.floor(0.2 * i)}px ${Math.floor(i * 0.25)}px rgba(0,0,0,0.1)`)
  ] as ThemeOptions['shadows'],
  mixins: {
    toolbar: {
      minHeight: 60
    }
  }
})

export default theme
